/**
 * Currency API Service
 * Handles currency-related API calls (public endpoints, not admin-only)
 */

import supabase from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get authentication headers with JWT token
 * @returns {Promise<Object>} Headers object with Authorization token
 */
async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json'
  };
}

/**
 * Get all exchange rates for a base currency
 * Public endpoint: GET /api/currency/rates
 *
 * @param {string} base - Base currency code (default: SGD)
 * @param {boolean} refresh - Force refresh from API (default: false)
 * @returns {Promise<Object>} Exchange rates with metadata
 */
export async function getExchangeRates(base = 'SGD', refresh = false) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams({
    base: base.toUpperCase(),
    refresh: refresh ? 'true' : 'false'
  });

  const url = `${API_URL}/api/currency/rates?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders
  });

  let data;
  try {
    data = await response.json();
  } catch (parseError) {
    console.error('Failed to parse response JSON:', parseError);
    throw new Error('Server returned invalid JSON response');
  }

  if (!response.ok) {
    const errorMessage = data?.message || data?.error || 'Failed to fetch exchange rates';
    throw new Error(errorMessage);
  }

  // Validate that response has the expected structure
  if (!data.data || !Array.isArray(data.data.rates)) {
    console.error('Invalid response structure:', data);
    throw new Error('Invalid response structure: missing data.rates array');
  }

  return data;
}
